import type { ComponentProps } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { VisuallyHidden } from "radix-ui";
import { useForm } from "react-hook-form";
import { t } from "ttag";

import { editWeightEntry } from "@/app/api/legacy-api";
import { LogoLoader } from "@/shared/components";
import { WeightEntryInput } from "@/shared/domains";
import type { WeightEntry } from "@/shared/domains";
import {
  MAX_ENTRY_NOTE_CHARACTERS,
  UNIT_SYSTEM,
  WEIGHT_UNIT,
} from "@/shared/models";
import { useSettingsStore } from "@/shared/stores";
import {
  Button,
  Dialog,
  errorToast,
  Form,
  GraphIcon,
  Input,
  TextArea,
  useToastStore,
} from "@/shared/ui";
import {
  convertKgToPounds,
  formatBackendDate,
  handleAxiosFieldErrors,
} from "@/shared/utils";

interface EditWeightEntryProps extends ComponentProps<typeof Dialog.Root> {
  weightEntry: WeightEntry;
}

export const EditWeightEntry = ({
  weightEntry,
  onOpenChange,
  ...props
}: EditWeightEntryProps) => {
  const { pushToast } = useToastStore();
  const unitSystem = useSettingsStore((state) => state.unitSystem);
  const form = useForm({
    resolver: zodResolver(WeightEntryInput),
    defaultValues: {
      unitSystem,
      ...weightEntry,
    },
  });

  const queryClient = useQueryClient();
  const { mutate: editWeightEntryMutation, isPending } = useMutation({
    mutationFn: editWeightEntry.mutation,
    onSuccess: () => {
      onOpenChange?.(false);
      editWeightEntry.invalidates(queryClient);
      void pushToast({
        type: "success",
        title: t`Success`,
        message: t`Weight entry successfully updated!`,
      });
    },
    onError: (err) => {
      errorToast(err);
      handleAxiosFieldErrors(err, form.setError);
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    const { weight, unitSystem, ...rest } = values;

    const weightInLbs =
      unitSystem === UNIT_SYSTEM.METRIC ? convertKgToPounds(weight) : weight;

    editWeightEntryMutation({
      id: weightEntry.id,
      weight: weightInLbs,
      ...rest,
    });
  });

  const updateDisabled = !form.formState.isDirty || isPending;

  return (
    <Dialog.Root onOpenChange={onOpenChange} {...props}>
      <Dialog.Content>
        <Dialog.Header icon={GraphIcon}>
          <Dialog.Title>{t`Edit entry`}</Dialog.Title>

          <VisuallyHidden.Root asChild>
            <Dialog.Description>
              {t`This modal allows you to update an existing weight entry. Modify the weight value, date, or note as needed.`}
            </Dialog.Description>
          </VisuallyHidden.Root>
        </Dialog.Header>

        <Form.Provider {...form}>
          <form onSubmit={handleSubmit} id="edit-entry-form" className="grow">
            <Form.Field
              control={form.control}
              name="date"
              render={() => (
                <Form.Item>
                  <Form.Label aria-required>{t`Date`}</Form.Label>
                  <Form.Control>
                    <Input.Root
                      readOnly
                      value={formatBackendDate(form.getValues("date"))}
                    />
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <Form.Field
              control={form.control}
              name="weight"
              render={() => (
                <Form.Item>
                  <Form.Label aria-required>
                    {t`Weight (${WEIGHT_UNIT[unitSystem]})`}
                  </Form.Label>
                  <Form.Control>
                    <Input.Root
                      placeholder={t`Your weight`}
                      type="number"
                      step=".01"
                      {...form.register("weight", { setValueAs: Number })}
                    />
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <Form.Field
              control={form.control}
              name="note"
              render={({ field }) => (
                <Form.Item>
                  <Form.Label>{t`Note (optional)`}</Form.Label>
                  <Form.Control>
                    <TextArea
                      placeholder={t`Optional note`}
                      rows={4}
                      {...field}
                    />
                  </Form.Control>
                  <div className="-mt-2 flex h-fit justify-between gap-2">
                    <Form.Message compact={false} className="mt-0" />
                    <Form.CharCounter max={MAX_ENTRY_NOTE_CHARACTERS} />
                  </div>
                </Form.Item>
              )}
            />
          </form>
        </Form.Provider>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="secondary" size="lg">
              {t`Cancel`}
            </Button>
          </Dialog.Close>

          <Button
            disabled={updateDisabled}
            type="submit"
            form="edit-entry-form"
            size="lg"
          >
            {t`Save changes`}
          </Button>
        </Dialog.Footer>

        <LogoLoader show={isPending} fullscreen />
      </Dialog.Content>
    </Dialog.Root>
  );
};
